import React from 'react';
import { View, Text, StyleSheet, Platform, ViewStyle, StyleProp } from 'react-native';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import {
  getTicketStatusInfo,
  getTicketTitleAndBody,
  getCategoryName,
  getDisplayDate,
  formatTicketDate,
} from '@/utils/residents/maintenance';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { useHover } from '@/hooks/common/useHover';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { WEB_STYLES } from '@/utils/webStyles';
import { PressableScale } from '@/components/shared/redesign/PressableScale';
import { GlassCard } from '../GlassCard';
import { buildCardHoverStyle } from './buildCardHoverStyle';

interface MaintenanceRequestRowProps {
  ticket: IMaintenanceListResponse;
  onPress: () => void;
  /** Closed rows render at reduced emphasis. */
  dimmed?: boolean;
  /** Desktop grid sizing; unset on mobile (full width). */
  width?: number;
  style?: StyleProp<ViewStyle>;
}

/** Compact one-line request row: status dot, title, category · date, chevron. */
export const MaintenanceRequestRow: React.FC<MaintenanceRequestRowProps> = React.memo(({
  ticket,
  onPress,
  dimmed = false,
  width,
  style,
}) => {
  const { colors } = useThemeColors();
  const { isHovered, hoverProps } = useHover();
  const reduceMotion = useReducedMotion();

  const info = getTicketStatusInfo(ticket);
  const isCompleted = info.status === 'completed';
  const statusColor = isCompleted ? colors.accent.green : info.color;
  const { title } = getTicketTitleAndBody(ticket);
  const category = getCategoryName(ticket);
  const displayDate = formatTicketDate(getDisplayDate(ticket), 'medium', '');

  const hoverLift = buildCardHoverStyle({
    isHovered,
    reduceMotion,
    isWeb: Platform.OS === 'web',
    shadowHover: colors.card.shadowHover,
    liftPx: 1,
  });

  return (
    <PressableScale
      onPress={onPress}
      scaleTo={0.98}
      {...hoverProps}
      containerStyle={[width ? { width } : undefined, style]}
      style={({ pressed }) => [
        styles.wrapper,
        dimmed && styles.dimmed,
        webWrapperStyle,
        hoverLift,
        pressed ? { opacity: 0.85 } : null,
      ]}
      accessibilityRole="button"
      accessibilityLabel={`${title}, ${info.label}`}
    >
      <GlassCard borderRadius={12} padding={14}>
        <View style={styles.row}>
          <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
          <View style={styles.body}>
            <Text style={[styles.title, { color: colors.text.primary }]} numberOfLines={1}>
              {title}
            </Text>
            <Text style={[styles.meta, { color: colors.text.dim }]} numberOfLines={1}>
              {category}{displayDate ? ` · ${displayDate}` : ''} · {info.label}
            </Text>
          </View>
          {isCompleted && (
            <View style={[styles.completedIcon, { backgroundColor: colors.accent.greenLight }]}>
              <RESIDENTS_SVG_COMPONENTS.CHECK width={10} height={10} color={colors.accent.green} />
            </View>
          )}
          <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT width={14} height={14} color={colors.text.dim} />
        </View>
      </GlassCard>
    </PressableScale>
  );
});

MaintenanceRequestRow.displayName = 'MaintenanceRequestRow';

const webWrapperStyle =
  Platform.OS === 'web'
    ? ({ cursor: 'pointer', ...WEB_STYLES.transition('all', '0.15s', 'ease') } as any)
    : undefined;

const styles = StyleSheet.create({
  wrapper: { borderRadius: 12 },
  dimmed: { opacity: 0.65 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  statusDot: { width: 8, height: 8, borderRadius: 4 },
  body: { flex: 1, gap: 2, minWidth: 0 },
  title: { ...REDESIGN_TYPOGRAPHY.body, fontWeight: '500', lineHeight: 20 },
  meta: { ...REDESIGN_TYPOGRAPHY.caption, lineHeight: 16 },
  completedIcon: { width: 18, height: 18, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
});
